// Single source of truth for US state codes / names / URL slugs.
// Consumed by the public Elections hub state directory chips, the state-page
// route validator, JSON-LD generators, the sitemap build script, the prerender
// `react-snap` include list, and the build-time DW data fetcher.
//
// Slug is the lowercase, hyphen-separated full state name (e.g.
// /elections/california, /elections/district-of-columbia). Full-name slugs
// outperform two-letter abbreviations on long-tail queries like
// "california 2026 elections" and are the convention used by the leading
// state-election guides Google ranks today.
// 51 entries: 50 states + DC.

export const US_STATES = [
  { code: "AL", name: "Alabama", slug: "alabama" },
  { code: "AK", name: "Alaska", slug: "alaska" },
  { code: "AZ", name: "Arizona", slug: "arizona" },
  { code: "AR", name: "Arkansas", slug: "arkansas" },
  { code: "CA", name: "California", slug: "california" },
  { code: "CO", name: "Colorado", slug: "colorado" },
  { code: "CT", name: "Connecticut", slug: "connecticut" },
  { code: "DE", name: "Delaware", slug: "delaware" },
  { code: "DC", name: "District of Columbia", slug: "district-of-columbia" },
  { code: "FL", name: "Florida", slug: "florida" },
  { code: "GA", name: "Georgia", slug: "georgia" },
  { code: "HI", name: "Hawaii", slug: "hawaii" },
  { code: "ID", name: "Idaho", slug: "idaho" },
  { code: "IL", name: "Illinois", slug: "illinois" },
  { code: "IN", name: "Indiana", slug: "indiana" },
  { code: "IA", name: "Iowa", slug: "iowa" },
  { code: "KS", name: "Kansas", slug: "kansas" },
  { code: "KY", name: "Kentucky", slug: "kentucky" },
  { code: "LA", name: "Louisiana", slug: "louisiana" },
  { code: "ME", name: "Maine", slug: "maine" },
  { code: "MD", name: "Maryland", slug: "maryland" },
  { code: "MA", name: "Massachusetts", slug: "massachusetts" },
  { code: "MI", name: "Michigan", slug: "michigan" },
  { code: "MN", name: "Minnesota", slug: "minnesota" },
  { code: "MS", name: "Mississippi", slug: "mississippi" },
  { code: "MO", name: "Missouri", slug: "missouri" },
  { code: "MT", name: "Montana", slug: "montana" },
  { code: "NE", name: "Nebraska", slug: "nebraska" },
  { code: "NV", name: "Nevada", slug: "nevada" },
  { code: "NH", name: "New Hampshire", slug: "new-hampshire" },
  { code: "NJ", name: "New Jersey", slug: "new-jersey" },
  { code: "NM", name: "New Mexico", slug: "new-mexico" },
  { code: "NY", name: "New York", slug: "new-york" },
  { code: "NC", name: "North Carolina", slug: "north-carolina" },
  { code: "ND", name: "North Dakota", slug: "north-dakota" },
  { code: "OH", name: "Ohio", slug: "ohio" },
  { code: "OK", name: "Oklahoma", slug: "oklahoma" },
  { code: "OR", name: "Oregon", slug: "oregon" },
  { code: "PA", name: "Pennsylvania", slug: "pennsylvania" },
  { code: "RI", name: "Rhode Island", slug: "rhode-island" },
  { code: "SC", name: "South Carolina", slug: "south-carolina" },
  { code: "SD", name: "South Dakota", slug: "south-dakota" },
  { code: "TN", name: "Tennessee", slug: "tennessee" },
  { code: "TX", name: "Texas", slug: "texas" },
  { code: "UT", name: "Utah", slug: "utah" },
  { code: "VT", name: "Vermont", slug: "vermont" },
  { code: "VA", name: "Virginia", slug: "virginia" },
  { code: "WA", name: "Washington", slug: "washington" },
  { code: "WV", name: "West Virginia", slug: "west-virginia" },
  { code: "WI", name: "Wisconsin", slug: "wisconsin" },
  { code: "WY", name: "Wyoming", slug: "wyoming" },
];

// Legacy two-letter URL slugs (e.g. /elections/ca) are kept here so old
// inbound links and any cached search-engine results redirect to the new
// canonical full-name slug instead of 404'ing.
const _LEGACY_SLUG_TO_CANONICAL = Object.freeze(
  US_STATES.reduce((acc, s) => {
    acc[s.code.toLowerCase()] = s.slug;
    return acc;
  }, {})
);

export function canonicalSlugForLegacy(legacy) {
  if (!legacy) return null;
  const key = String(legacy).toLowerCase();
  return _LEGACY_SLUG_TO_CANONICAL[key] || null;
}

const _BY_CODE = Object.freeze(
  US_STATES.reduce((acc, s) => {
    acc[s.code] = s;
    return acc;
  }, {})
);

const _BY_SLUG = Object.freeze(
  US_STATES.reduce((acc, s) => {
    acc[s.slug] = s;
    return acc;
  }, {})
);

export function stateByCode(code) {
  if (!code) return null;
  return _BY_CODE[String(code).toUpperCase()] || null;
}

export function stateBySlug(slug) {
  if (!slug) return null;
  return _BY_SLUG[String(slug).toLowerCase()] || null;
}

export function stateNameByCode(code) {
  const entry = stateByCode(code);
  return entry ? entry.name : "";
}

export function isValidStateCode(code) {
  if (!code) return false;
  return Boolean(_BY_CODE[String(code).toUpperCase()]);
}

export function isValidStateSlug(slug) {
  if (!slug) return false;
  return Boolean(_BY_SLUG[String(slug).toLowerCase()]);
}

// Convenience helpers used by the sitemap script and the prerender include list
export const ALL_STATE_SLUGS = US_STATES.map((s) => s.slug);
export const ALL_STATE_CODES = US_STATES.map((s) => s.code);

// US Census Bureau regional groupings — used to surface "related states" on
// each /elections/:slug page for topical clustering and PageRank flow.
const _REGION_BY_CODE = {
  // Northeast
  CT: "Northeast", ME: "Northeast", MA: "Northeast", NH: "Northeast",
  NJ: "Northeast", NY: "Northeast", PA: "Northeast", RI: "Northeast",
  VT: "Northeast",
  // Midwest
  IL: "Midwest", IN: "Midwest", IA: "Midwest", KS: "Midwest", MI: "Midwest",
  MN: "Midwest", MO: "Midwest", NE: "Midwest", ND: "Midwest", OH: "Midwest",
  SD: "Midwest", WI: "Midwest",
  // South
  AL: "South", AR: "South", DE: "South", DC: "South", FL: "South",
  GA: "South", KY: "South", LA: "South", MD: "South", MS: "South",
  NC: "South", OK: "South", SC: "South", TN: "South", TX: "South",
  VA: "South", WV: "South",
  // West
  AK: "West", AZ: "West", CA: "West", CO: "West", HI: "West", ID: "West",
  MT: "West", NV: "West", NM: "West", OR: "West", UT: "West", WA: "West",
  WY: "West",
};

export function regionForCode(code) {
  if (!code) return null;
  return _REGION_BY_CODE[String(code).toUpperCase()] || null;
}

// Returns up to `limit` neighboring states in the same Census region as the
// given state (excluding the state itself). Order is alphabetical by name
// for stable, prerender-friendly output.
export function relatedStates(code, limit = 8) {
  const region = regionForCode(code);
  if (!region) return [];
  const upper = String(code).toUpperCase();
  return US_STATES.filter(
    (s) => _REGION_BY_CODE[s.code] === region && s.code !== upper
  )
    .sort((a, b) => a.name.localeCompare(b.name))
    .slice(0, limit);
}
